import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Settings as SettingsIcon } from 'lucide-react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useAchievements } from '../hooks/useAchievements';
import { useProfileBySlug } from '../hooks/useProfile';
import { Avatar } from '../components/ui/Avatar';
import { MedalIcon } from '../components/ui/MedalIcon';
import { Eyebrow } from '../components/ui/Eyebrow';
import { Pill } from '../components/ui/Pill';
import { MedalDetailModal } from '../components/MedalDetailModal';
import { TOTAL_STICKERS } from '../data/stickers';
import { longestStreak } from '../lib/achievements';
import type {
  Achievement,
  AchievementCategory,
  PublicProfile,
  UserAchievement,
} from '../types/social';

interface Props {
  user: User;
}

const CATEGORY_FILTERS: Array<{ key: 'all' | AchievementCategory; label: string }> = [
  { key: 'all', label: 'Todas' },
  { key: 'collection', label: 'Coleção' },
  { key: 'confed', label: 'Confederações' },
  { key: 'social', label: 'Social' },
  { key: 'time', label: 'Tempo' },
  { key: 'event', label: 'Eventos' },
  { key: 'special', label: 'Especial' },
];

interface Stats {
  owned: number;
  duplicates: number;
  trades: number;
  streak: number;
}

interface SelfFriendData {
  name: string;
  email: string;
  isSelf: boolean;
  isFriend: boolean;
  stats: Stats;
}

async function loadSelfOrFriendData(
  targetUserId: string,
  currentUserId: string,
  authUser: User
): Promise<SelfFriendData | null> {
  const isSelf = targetUserId === currentUserId;

  const { data: stickers } = await supabase
    .from('stickers')
    .select('sticker_code, count')
    .eq('user_id', targetUserId);

  const counts: Record<string, number> = {};
  for (const r of (stickers ?? []) as Array<{ sticker_code: string; count: number }>) {
    counts[r.sticker_code] = r.count;
  }
  const owned = Object.values(counts).filter((c) => c >= 1).length;
  const duplicates = Object.values(counts).filter((c) => c >= 2).length;

  const { count: tradesCount } = await supabase
    .from('trades')
    .select('id', { count: 'exact', head: true })
    .or(`user_a.eq.${targetUserId},user_b.eq.${targetUserId}`)
    .eq('status', 'confirmed');

  const { data: activity } = await supabase
    .from('daily_activity')
    .select('date')
    .eq('user_id', targetUserId)
    .order('date', { ascending: false })
    .limit(120);
  const streak = longestStreak((activity ?? []).map((r: { date: string }) => r.date));

  let name = '';
  let email = '';
  let isFriend = false;
  if (isSelf) {
    name = (authUser.user_metadata?.full_name as string) || authUser.email || 'Você';
    email = authUser.email ?? '';
  } else {
    const { data: f } = await supabase
      .from('friendships')
      .select('friend_name, friend_email')
      .eq('user_id', currentUserId)
      .eq('friend_id', targetUserId)
      .maybeSingle();
    if (f) {
      isFriend = true;
      name =
        (f as { friend_name?: string; friend_email?: string }).friend_name ||
        (f as { friend_email?: string }).friend_email ||
        'Amigo';
      email = (f as { friend_email?: string }).friend_email || '';
    } else {
      return null;
    }
  }

  return {
    name,
    email,
    isSelf,
    isFriend,
    stats: { owned, duplicates, trades: tradesCount ?? 0, streak },
  };
}

export default function ProfilePage({ user }: Props) {
  const { userId: paramUserId, slug: paramSlug } = useParams<{ userId?: string; slug?: string }>();

  const { profile: bySlug, loading: slugLoading, notFound: slugNotFound } = useProfileBySlug(paramSlug);
  const targetUserId = paramSlug
    ? bySlug?.user_id
    : (paramUserId || user.id);

  const isSelfRoute = !paramSlug && !paramUserId;

  return slugLoading ? (
    <div className="min-h-screen bg-paper flex items-center justify-center">
      <Loader2 size={28} className="animate-spin text-green-700" />
    </div>
  ) : paramSlug && slugNotFound ? (
    <NotFoundProfile />
  ) : !targetUserId ? (
    <NotFoundProfile />
  ) : (
    <ProfileContent
      user={user}
      targetUserId={targetUserId}
      publicProfile={bySlug}
      isSelfRoute={isSelfRoute}
    />
  );
}

interface ContentProps {
  user: User;
  targetUserId: string;
  publicProfile: PublicProfile | null;
  isSelfRoute: boolean;
}

function ProfileContent({ user, targetUserId, isSelfRoute }: ContentProps) {
  const isSelf = targetUserId === user.id;

  const [selfFriend, setSelfFriend] = useState<SelfFriendData | null>(null);
  const [loadingSF, setLoadingSF] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingSF(true);
    setAccessDenied(false);
    void (async () => {
      const data = await loadSelfOrFriendData(targetUserId, user.id, user);
      if (cancelled) return;
      if (!data) {
        setAccessDenied(true);
        setSelfFriend(null);
      } else {
        setSelfFriend(data);
      }
      setLoadingSF(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [targetUserId, user]);

  if (loadingSF) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-green-700" />
      </div>
    );
  }

  if (selfFriend) {
    return (
      <SelfFriendView
        user={user}
        data={selfFriend}
        showBackToAlbum={isSelfRoute || isSelf}
      />
    );
  }

  if (accessDenied) {
    return <NotFoundProfile />;
  }

  return <NotFoundProfile />;
}

function NotFoundProfile() {
  return (
    <div className="min-h-screen bg-paper flex flex-col items-center justify-center px-6 text-center">
      <p className="font-display font-semibold text-h2 text-ink-900 mb-2">Perfil indisponível</p>
      <p className="font-body text-small text-ink-500 mb-4">
        Esse perfil é privado ou não é seu amigo no app.
      </p>
      <Link to="/album?tab=amigos" className="text-green-700 font-semibold">
        Voltar
      </Link>
    </div>
  );
}

function SelfFriendView({
  user,
  data,
  showBackToAlbum,
}: {
  user: User;
  data: SelfFriendData;
  showBackToAlbum: boolean;
}) {
  const navigate = useNavigate();
  const targetUserId = data.isSelf ? user.id : '';
  const { catalog, earned, loading: achLoading } = useAchievements(
    data.isSelf ? user.id : targetUserId
  );
  const [filter, setFilter] = useState<'all' | AchievementCategory>('all');
  const [selectedMedal, setSelectedMedal] = useState<Achievement | null>(null);

  const earnedById = useMemo(() => {
    const m = new Map<string, UserAchievement>();
    for (const e of earned) m.set(e.achievement_id, e);
    return m;
  }, [earned]);

  const filteredCatalog = useMemo(() => {
    if (filter === 'all') return catalog;
    return catalog.filter((a) => a.category === filter);
  }, [catalog, filter]);

  const pct = TOTAL_STICKERS > 0 ? Math.round((data.stats.owned / TOTAL_STICKERS) * 100) : 0;
  const earnedCount = earned.length;

  return (
    <div className="min-h-screen bg-paper">
      <header className="bg-green-700 text-white sticky top-0 z-30 border-b border-white/10">
        <div className="max-w-app mx-auto px-4 py-3.5 flex items-center gap-3">
          <button
            type="button"
            onClick={() => (showBackToAlbum ? navigate('/album') : navigate(-1))}
            aria-label="Voltar"
            className="inline-flex items-center justify-center w-9 h-9 rounded-md border-[1.5px] border-white/40 hover:bg-white/10 btn-focus"
          >
            <ArrowLeft size={16} strokeWidth={2.25} />
          </button>
          <h1 className="font-display font-semibold text-[18px]">
            {data.isSelf ? 'Meu perfil' : data.name.split(/\s+/)[0]}
          </h1>
          {data.isSelf && (
            <Link
              to="/configuracoes"
              aria-label="Configurações"
              className="ml-auto inline-flex items-center justify-center w-9 h-9 rounded-md border-[1.5px] border-white/40 hover:bg-white/10 btn-focus"
            >
              <SettingsIcon size={15} strokeWidth={2.25} />
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-app mx-auto px-4 pt-6 pb-32">
        <div className="flex flex-col items-center text-center mb-7">
          <Avatar name={data.name} size="xl" />
          <h2 className="font-display font-bold text-h1 text-ink-900 mt-3 tracking-[-0.015em]">
            {data.name}
          </h2>
          {earnedCount >= 5 && (
            <Pill bg="#F2D680" fg="#5C3F02" size="md" className="mt-2">
              {earnedCount} medalhas
            </Pill>
          )}
        </div>

        <section className="grid grid-cols-4 gap-2 mb-6">
          <StatCell label="Figurinhas" value={`${data.stats.owned}`} sub={`/${TOTAL_STICKERS}`} />
          <StatCell label="Repetidas" value={`${data.stats.duplicates}`} />
          <StatCell label="Trocas" value={`${data.stats.trades}`} />
          <StatCell label="Streak" value={`${data.stats.streak}`} sub="dias" />
        </section>

        <section className="mb-7">
          <div className="flex items-baseline justify-between mb-2">
            <Eyebrow>Álbum</Eyebrow>
            <span className="font-display font-bold text-h3 text-green-700">{pct}%</span>
          </div>
          <div className="w-full h-2 bg-ink-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-700 transition-[width] duration-progress ease-out"
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
        </section>

        <section>
          <div className="flex items-baseline justify-between mb-3">
            <Eyebrow>Medalhas</Eyebrow>
            <span className="text-caption text-ink-500 font-semibold">
              {earnedCount}/{catalog.length} conquistadas
            </span>
          </div>

          <div
            className="flex gap-1.5 overflow-x-auto -mx-4 px-4 pb-2 mb-4"
            style={{ scrollbarWidth: 'none' }}
          >
            {CATEGORY_FILTERS.map((c) => {
              const active = filter === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setFilter(c.key)}
                  className={[
                    'shrink-0 rounded-full px-3 py-1.5 text-caption font-semibold border transition-colors',
                    active
                      ? 'bg-green-700 text-white border-green-700'
                      : 'bg-white text-ink-700 border-ink-300 hover:border-green-700',
                  ].join(' ')}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          {achLoading ? (
            <div className="grid grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-square bg-ink-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filteredCatalog.length === 0 ? (
            <p className="text-small text-ink-500 italic text-center py-6">
              Sem medalhas nessa categoria.
            </p>
          ) : (
            <div className="grid grid-cols-4 gap-3">
              {filteredCatalog.map((m) => {
                const locked = !earnedById.has(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setSelectedMedal(m)}
                    aria-label={m.name}
                    className="flex flex-col items-center gap-1.5 p-2 rounded-xl bg-white border border-ink-300 hover:border-green-700 transition-colors btn-focus"
                  >
                    <MedalIcon medal={m} size={56} locked={locked} />
                    <span
                      className={[
                        'font-display font-semibold text-[11px] leading-tight text-center line-clamp-2',
                        locked ? 'text-ink-500' : 'text-ink-900',
                      ].join(' ')}
                    >
                      {m.name}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <MedalDetailModal
        medal={selectedMedal}
        earnedAt={selectedMedal ? earnedById.get(selectedMedal.id)?.earned_at ?? null : null}
        locked={selectedMedal ? !earnedById.has(selectedMedal.id) : false}
        onClose={() => setSelectedMedal(null)}
      />
    </div>
  );
}

interface StatCellProps {
  label: string;
  value: string;
  sub?: string;
}

function StatCell({ label, value, sub }: StatCellProps) {
  return (
    <div className="bg-white border border-ink-300 rounded-xl px-2 py-3 text-center shadow-xs">
      <div className="font-display font-bold text-[18px] text-ink-900 leading-tight">
        {value}
        {sub && <span className="text-caption text-ink-500 font-semibold ml-0.5">{sub}</span>}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-ink-500 font-bold mt-1">
        {label}
      </div>
    </div>
  );
}
